/**
 * Reviews carousel for the home page.
 * Slides scroll natively (swipe, trackpad, arrow keys on the focused track); this adds the dots and the
 * previous/next buttons and keeps the active dot in sync with the scroll position.
 */
if (!customElements.get('sustena-testimonials-carousel')) {
  class SustenaTestimonialsCarousel extends HTMLElement {
    #reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    /** @type {HTMLElement | null} */
    #track = null;
    /** @type {HTMLElement[]} */
    #slides = [];
    /** @type {HTMLButtonElement[]} */
    #dots = [];
    #frame = 0;

    connectedCallback() {
      this.#track = this.querySelector('[data-track]');
      if (!this.#track) return;
      this.#slides = Array.from(this.#track.querySelectorAll('[data-slide]'));
      this.#dots = Array.from(this.querySelectorAll('[data-dot]'));
      if (this.#slides.length < 2) return;

      for (const control of this.querySelectorAll('[data-controls]')) control.removeAttribute('hidden');

      this.#dots.forEach((dot, index) => dot.addEventListener('click', () => this.#scrollToSlide(index)));
      this.querySelector('[data-previous]')?.addEventListener('click', this.#previous);
      this.querySelector('[data-next]')?.addEventListener('click', this.#next);
      this.#track.addEventListener('scroll', this.#onScroll, { passive: true });
      this.#update();
    }

    disconnectedCallback() {
      this.#track?.removeEventListener('scroll', this.#onScroll);
      cancelAnimationFrame(this.#frame);
    }

    /** Scroll position of a slide, measured from the first one */
    /** @param {number} index */
    #offsetOf(index) {
      const first = this.#slides[0];
      const slide = this.#slides[index];
      return first && slide ? slide.offsetLeft - first.offsetLeft : 0;
    }

    #maxScroll() {
      return this.#track ? this.#track.scrollWidth - this.#track.clientWidth : 0;
    }

    #currentIndex() {
      if (!this.#track) return 0;
      const position = this.#track.scrollLeft;
      if (position >= this.#maxScroll() - 2) return this.#slides.length - 1;
      const step = this.#offsetOf(1) || 1;
      return Math.max(0, Math.min(this.#slides.length - 1, Math.round(position / step)));
    }

    /** @param {number} index */
    #scrollToSlide(index) {
      this.#track?.scrollTo({ left: this.#offsetOf(index), behavior: this.#reducedMotion.matches ? 'auto' : 'smooth' });
    }

    #previous = () => {
      if (!this.#track) return;
      if (this.#track.scrollLeft <= 2) {
        this.#track.scrollTo({ left: this.#maxScroll(), behavior: this.#reducedMotion.matches ? 'auto' : 'smooth' });
      } else {
        this.#scrollToSlide(Math.max(0, this.#currentIndex() - 1));
      }
    };

    #next = () => {
      if (!this.#track) return;
      if (this.#track.scrollLeft >= this.#maxScroll() - 2) {
        this.#scrollToSlide(0);
      } else {
        this.#scrollToSlide(this.#currentIndex() + 1);
      }
    };

    #onScroll = () => {
      cancelAnimationFrame(this.#frame);
      this.#frame = requestAnimationFrame(() => this.#update());
    };

    #update() {
      const active = this.#currentIndex();
      this.#dots.forEach((dot, index) => {
        if (index === active) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      });
    }
  }

  customElements.define('sustena-testimonials-carousel', SustenaTestimonialsCarousel);
}
